<template>
  <ErrorBoundary>
    <div v-if="isLoading" class="min-h-full bg-slate-50 pb-12">
      <div class="max-w-3xl mx-auto px-4 py-6 space-y-4">
        <!-- 标题骨架 -->
        <div class="bg-white rounded-lg shadow-sm p-6 animate-pulse">
          <div class="flex gap-2 mb-3">
            <div class="w-16 h-6 bg-slate-200 rounded-full" />
            <div class="w-12 h-6 bg-slate-200 rounded-full" />
          </div>
          <div class="w-3/4 h-8 bg-slate-200 rounded mb-4" />
          <div class="w-12 h-0.5 bg-slate-200 mb-4" />
          <div class="flex gap-4">
            <div class="w-20 h-4 bg-slate-200 rounded" />
            <div class="w-24 h-4 bg-slate-200 rounded" />
          </div>
        </div>

        <!-- 内容骨架 -->
        <div class="bg-white rounded-lg shadow-sm p-6 animate-pulse">
          <div class="w-32 h-5 bg-slate-200 rounded mb-3" />
          <div class="w-12 h-0.5 bg-slate-200 mb-4" />
          <div class="space-y-2">
            <div class="w-full h-4 bg-slate-200 rounded" />
            <div class="w-full h-4 bg-slate-200 rounded" />
            <div class="w-2/3 h-4 bg-slate-200 rounded" />
          </div>
        </div>
      </div>
    </div>

    <div v-else class="min-h-full bg-slate-50 pb-12">
      <div class="max-w-3xl mx-auto px-4 py-6 space-y-4">

        <!-- 1. 标题卡片 -->
        <div class="bg-white rounded-lg shadow-sm p-6 animate-fade-in-up">
          <div class="flex items-center gap-2 mb-3">
            <span class="px-3 py-1 bg-amber-50 text-amber-600 text-xs font-bold rounded-full">
              {{ topic.type }}
            </span>
            <span v-if="topic.isHot" class="px-3 py-1 bg-red-100 text-red-600 text-xs font-bold rounded-full">
              HOT
            </span>
          </div>

          <h1 class="text-2xl md:text-3xl font-black text-slate-900 leading-tight mb-4 break-words">
            {{ topic.title }}
          </h1>

          <!-- 金色分隔线 -->
          <div class="w-12 h-0.5 bg-amber-400 mb-4"></div>

          <!-- 元信息 -->
          <div class="flex items-center gap-4 text-xs text-slate-400">
            <span class="flex items-center gap-1">
              <IconVideo class="w-3.5 h-3.5" />
              {{ topic.views ? topic.views.toLocaleString() : '1,234' }} 浏览
            </span>
            <span class="flex items-center gap-1">
              <IconSparkles class="w-3.5 h-3.5" />
              AI 智能解析
            </span>
          </div>
        </div>

        <!-- 2. AI 智能解析算法卡片 -->
        <div class="bg-white rounded-lg shadow-sm p-6 animate-fade-in-up" style="animation-delay: 0.1s">
          <!-- 标题区 -->
          <div class="flex items-center gap-2 mb-3">
            <IconSparkles class="w-4 h-4 text-amber-400" />
            <h3 class="text-sm font-black text-slate-900">
              {{ TOPIC_DETAIL_MESSAGES.aiAnalysis.title }}
            </h3>
          </div>

          <!-- 金色分隔线 -->
          <div class="w-12 h-0.5 bg-amber-400 mb-4"></div>

          <!-- 内容区 -->
          <div class="relative">
            <template v-if="topic.caseStudy">
              <div
                ref="caseStudyContent"
                class="overflow-hidden transition-all duration-300 ease-in-out"
                :style="{ height: isExpanded ? 'auto' : '6em' }"
              >
                <div class="text-slate-700 text-[15px] leading-relaxed">
                  {{ topic.caseStudy }}
                </div>
              </div>

              <div v-if="!isExpanded" class="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent pointer-events-none" />
            </template>
            <template v-else>
              <div class="flex flex-col items-center justify-center py-12 text-center">
                <div class="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                  <IconFileX class="w-8 h-8 text-slate-400" />
                </div>
                <h4 class="text-base font-bold text-slate-700 mb-2">
                  {{ TOPIC_DETAIL_MESSAGES.emptyCaseStudy.title }}
                </h4>
                <p class="text-sm text-slate-500 max-w-xs mb-4">
                  {{ TOPIC_DETAIL_MESSAGES.emptyCaseStudy.description }}
                </p>
              </div>
            </template>
          </div>

          <!-- 展开/收起按钮 + 操作按钮 -->
          <div v-if="topic.caseStudy" class="mt-4 flex items-center justify-between flex-wrap gap-3">
            <button
              @click="toggleExpand"
              class="flex items-center gap-1 text-sm text-amber-400 font-bold hover:text-amber-500 transition-colors"
              :aria-expanded="isExpanded"
            >
              {{ isExpanded ? TOPIC_DETAIL_MESSAGES.aiAnalysis.collapse : TOPIC_DETAIL_MESSAGES.aiAnalysis.expand }}
              <IconChevronDown v-if="isExpanded" class="w-4 h-4" />
              <IconChevronRight v-else class="w-4 h-4" />
            </button>

            <!-- 操作按钮栏 -->
            <div class="flex items-center gap-1">
              <button
                @click="handleCopy"
                class="flex items-center gap-1 px-3 py-1.5 text-xs text-slate-500 hover:text-amber-500 hover:bg-amber-50 rounded-lg transition-all"
                :title="TOPIC_DETAIL_MESSAGES.actions.copy"
              >
                <IconCopy class="w-3.5 h-3.5" />
                <span>{{ TOPIC_DETAIL_MESSAGES.actions.copy }}</span>
              </button>
              <button
                @click="handleLike"
                class="flex items-center gap-1 px-3 py-1.5 text-xs text-slate-500 hover:text-amber-500 hover:bg-amber-50 rounded-lg transition-all"
                :title="TOPIC_DETAIL_MESSAGES.actions.like"
              >
                <IconThumbsUp class="w-3.5 h-3.5" />
                <span>{{ TOPIC_DETAIL_MESSAGES.actions.like }}</span>
              </button>
              <button
                @click="handleDislike"
                class="flex items-center gap-1 px-3 py-1.5 text-xs text-slate-500 hover:text-slate-500 hover:bg-slate-100 rounded-lg transition-all"
                :title="TOPIC_DETAIL_MESSAGES.actions.dislike"
              >
                <IconThumbsDown class="w-3.5 h-3.5" />
                <span>{{ TOPIC_DETAIL_MESSAGES.actions.dislike }}</span>
              </button>
              <button
                @click="handleBookmark"
                class="flex items-center gap-1 px-3 py-1.5 text-xs text-slate-500 hover:text-amber-500 hover:bg-amber-50 rounded-lg transition-all"
                :title="TOPIC_DETAIL_MESSAGES.actions.bookmark"
              >
                <IconBookmark class="w-3.5 h-3.5" />
                <span>{{ TOPIC_DETAIL_MESSAGES.actions.bookmark }}</span>
              </button>
              <button
                @click="handleContribute"
                class="flex items-center gap-1 px-3 py-1.5 text-xs text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded-lg transition-all"
                :title="TOPIC_DETAIL_MESSAGES.actions.contribute"
              >
                <IconLightbulb class="w-3.5 h-3.5" />
                <span>{{ TOPIC_DETAIL_MESSAGES.actions.contribute }}</span>
              </button>
            </div>
          </div>
        </div>

        <!-- 3. 参考专家卡片 -->
        <div class="bg-white rounded-lg shadow-sm p-6 animate-fade-in-up" style="animation-delay: 0.2s">
          <!-- 标题区 -->
          <div class="flex items-center justify-between mb-3">
            <div class="flex items-center gap-2">
              <IconUsers class="w-4 h-4 text-amber-400" />
              <h3 class="text-sm font-black text-slate-900">
                {{ TOPIC_DETAIL_MESSAGES.expert.title }}
              </h3>
            </div>
            <!-- 桌面端滚动按钮 -->
            <div class="hidden md:flex items-center gap-1">
              <button
                @click="scrollExperts('left')"
                class="p-1.5 text-slate-400 hover:text-amber-500 hover:bg-amber-50 rounded-lg transition-all"
              >
                <IconChevronLeft class="w-4 h-4" />
              </button>
              <button
                @click="scrollExperts('right')"
                class="p-1.5 text-slate-400 hover:text-amber-500 hover:bg-amber-50 rounded-lg transition-all"
              >
                <IconArrowRight class="w-4 h-4" />
              </button>
            </div>
          </div>

          <!-- 金色分隔线 -->
          <div class="w-12 h-0.5 bg-amber-400 mb-4"></div>

          <!-- 专家列表 - 移动端横向滚动，桌面端网格 -->
          <div class="relative">
            <!-- 滚动提示阴影 -->
            <div class="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent pointer-events-none z-10 md:hidden" />

            <div
              id="expert-scroll-container"
              class="flex gap-4 overflow-x-auto pb-2 -mx-2 px-2 scrollbar-hide md:grid md:grid-cols-3 md:overflow-visible"
            >
              <template v-if="matchedExperts.length > 0">
                <div
                  v-for="expert in matchedExperts"
                  :key="expert.id"
                  class="flex-shrink-0 w-36 md:w-auto bg-slate-50 border border-slate-100 rounded-xl p-4 hover:shadow-md hover:border-amber-200 transition-all hover:-translate-y-1"
                >
                  <div class="flex flex-col items-center text-center">
                    <!-- 头像和姓名 - 可点击跳转 -->
                    <div
                      class="flex flex-col items-center cursor-pointer group"
                      @click="navigateToExpert(expert.id)"
                    >
                      <img
                        :src="expert.avatar"
                        class="w-14 h-14 rounded-full object-cover border-2 border-slate-100 mb-3 group-hover:border-amber-400 transition-colors"
                        :alt="expert.name"
                        referrerpolicy="no-referrer"
                      />
                      <div class="text-sm font-bold text-slate-900 mb-0.5 group-hover:text-amber-500 transition-colors line-clamp-1">
                        {{ expert.name }}
                      </div>
                    </div>

                    <!-- 部门 + 职务 -->
                    <div class="text-xs text-slate-500 mb-3 line-clamp-1" :title="`${expert.department || ''} · ${expert.position || expert.title || ''}`">
                      {{ expert.department ? `${expert.department} · ${expert.position || expert.title}` : (expert.position || expert.title) }}
                    </div>

                    <!-- 操作按钮 -->
                    <div class="flex gap-1.5 w-full">
                      <button
                        @click="navigateToExpertCase(expert.id, 'video')"
                        class="flex-1 flex items-center justify-center py-1.5 bg-white rounded-lg text-slate-400 hover:bg-amber-50 hover:text-amber-500 transition-all"
                        :title="TOPIC_DETAIL_MESSAGES.expert.video"
                      >
                        <IconVideo class="w-3.5 h-3.5" />
                      </button>
                      <button
                        @click="navigateToExpertCase(expert.id, 'audio')"
                        class="flex-1 flex items-center justify-center py-1.5 bg-white rounded-lg text-slate-400 hover:bg-amber-50 hover:text-amber-500 transition-all"
                        :title="TOPIC_DETAIL_MESSAGES.expert.audio"
                      >
                        <IconHeadphones class="w-3.5 h-3.5" />
                      </button>
                      <button
                        @click="navigateToExpertCase(expert.id, 'text')"
                        class="flex-1 flex items-center justify-center py-1.5 bg-white rounded-lg text-slate-400 hover:bg-amber-50 hover:text-amber-500 transition-all"
                        :title="TOPIC_DETAIL_MESSAGES.expert.text"
                      >
                        <IconFileText class="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              </template>
              <template v-else>
                <div class="w-full text-center py-8 text-slate-400 text-sm">
                  {{ TOPIC_DETAIL_MESSAGES.expert.noExpert }}
                </div>
              </template>
            </div>
          </div>
        </div>

        <!-- 4. 实战转化卡片 -->
        <div class="bg-white rounded-lg shadow-sm p-6 animate-fade-in-up" style="animation-delay: 0.3s">
          <!-- 标题区 -->
          <div class="flex items-center gap-2 mb-3">
            <IconZap class="w-4 h-4 text-amber-400" />
            <h3 class="text-sm font-black text-slate-900">
              {{ TOPIC_DETAIL_MESSAGES.practice.title }}
            </h3>
          </div>

          <!-- 金色分隔线 -->
          <div class="w-12 h-0.5 bg-amber-400 mb-4"></div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <!-- 聊一聊 -->
            <button
              @click="handleNavigateToChat"
              class="group p-4 bg-slate-50 rounded-xl border border-slate-100 hover:border-amber-200 hover:shadow-md transition-all text-left h-full"
            >
              <div class="w-10 h-10 bg-white rounded-lg shadow-sm flex items-center justify-center mb-3 group-hover:bg-amber-50 transition-colors">
                <IconActivity class="w-5 h-5 text-slate-400 group-hover:text-amber-500 transition-colors" />
              </div>
              <div class="text-sm font-bold text-slate-900 mb-1">
                {{ TOPIC_DETAIL_MESSAGES.practice.chatTitle }}
              </div>
              <div class="text-xs text-slate-500">
                {{ TOPIC_DETAIL_MESSAGES.practice.chatDesc }}
              </div>
            </button>

            <!-- 练一练 -->
            <button
              @click="handleNavigateToPractice"
              class="group p-4 bg-slate-50 rounded-xl border border-slate-100 hover:border-amber-200 hover:shadow-md transition-all text-left h-full"
            >
              <div class="w-10 h-10 bg-white rounded-lg shadow-sm flex items-center justify-center mb-3 group-hover:bg-amber-50 transition-colors">
                <IconSword class="w-5 h-5 text-slate-400 group-hover:text-amber-500 transition-colors" />
              </div>
              <div class="text-sm font-bold text-slate-900 mb-1">
                {{ TOPIC_DETAIL_MESSAGES.practice.practiceTitle }}
              </div>
              <div class="text-xs text-slate-500">
                {{ TOPIC_DETAIL_MESSAGES.practice.practiceDesc }}
              </div>
            </button>
          </div>
        </div>

        <!-- 5. 相关话题 -->
        <div v-if="topic.relatedIds && topic.relatedIds.length > 0" class="bg-white rounded-lg shadow-sm p-6 animate-fade-in-up" style="animation-delay: 0.4s">
          <!-- 标题区 -->
          <div class="flex items-center gap-2 mb-3">
            <IconLink2 class="w-4 h-4 text-amber-400" />
            <h3 class="text-sm font-black text-slate-900">
              {{ TOPIC_DETAIL_MESSAGES.relatedTopics.title }}
            </h3>
          </div>

          <!-- 分隔线 -->
          <div class="w-12 h-0.5 bg-amber-400 mb-4" />

          <div class="space-y-2">
            <button
              v-for="(relatedTopic, index) in relatedTopics"
              :key="relatedTopic.id"
              @click="navigateToTopic(relatedTopic.id)"
              class="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors text-left group"
            >
              <span class="text-xs font-bold text-amber-400/60 group-hover:text-amber-400 transition-colors">
                {{ (index + 1).toString().padStart(2, '0') }}
              </span>
              <span class="flex-1 text-sm text-slate-700 group-hover:text-slate-900 transition-colors line-clamp-1">
                {{ relatedTopic.title }}
              </span>
              <IconArrowRight class="w-4 h-4 text-slate-300 group-hover:text-amber-400 transition-colors" />
            </button>
          </div>
        </div>

      </div>

      <!-- Toast 提示 -->
      <Toast :toasts="toasts" @remove="removeToast" />

      <!-- 反馈弹窗 -->
      <FeedbackModal
        :is-open="feedbackModalOpen"
        @close="feedbackModalOpen = false"
        @submit="handleFeedbackSubmit"
      />

      <!-- 支招弹窗 -->
      <ContributeModal
        :is-open="contributeModalOpen"
        :topic-title="topic.title"
        @close="contributeModalOpen = false"
        @submit="handleContributeSubmit"
      />
    </div>
  </ErrorBoundary>
</template>

<script>
import {
  IconFileText,
  IconHeadphones,
  IconVideo,
  IconZap,
  IconActivity,
  IconSparkles,
  IconUsers,
  IconSword,
  IconLink2,
  IconChevronDown,
  IconChevronRight,
  IconThumbsUp,
  IconThumbsDown,
  IconBookmark,
  IconCopy,
  IconLightbulb,
  IconArrowRight,
  IconFileX,
  IconChevronLeft
} from '../icons'
import { calculateExpertMatches } from '../../services/ai-service'
import { TOPICS } from '../../data'
import { TOPIC_DETAIL_MESSAGES, TOPIC_DETAIL_CONSTANTS } from '../../constants/messages'
import ErrorBoundary from '../common/ErrorBoundary.vue'
import Toast from '../common/Toast.vue'
import FeedbackModal from '../feedback/FeedbackModal.vue'
import ContributeModal from '../user/ContributeModal.vue'

export default {
  name: 'StudyDetailView',
  components: {
    IconFileText,
    IconHeadphones,
    IconVideo,
    IconZap,
    IconActivity,
    IconSparkles,
    IconUsers,
    IconSword,
    IconLink2,
    IconChevronDown,
    IconChevronRight,
    IconThumbsUp,
    IconThumbsDown,
    IconBookmark,
    IconCopy,
    IconLightbulb,
    IconArrowRight,
    IconFileX,
    IconChevronLeft,
    ErrorBoundary,
    Toast,
    FeedbackModal,
    ContributeModal
  },
  props: {
    topic: {
      type: Object,
      required: true
    },
    experts: {
      type: Array,
      default: () => []
    },
    context: {
      type: Object,
      default: () => ({})
    },
    onNavigateToPractice: {
      type: Function,
      default: () => {}
    },
    onNavigateToDiagnosis: {
      type: Function,
      default: () => {}
    }
  },
  data() {
    return {
      isExpanded: false,
      isLoading: false,
      feedbackModalOpen: false,
      contributeModalOpen: false,
      toasts: [],
      TOPIC_DETAIL_MESSAGES,
      MAX_EXPERTS_DISPLAY: TOPIC_DETAIL_CONSTANTS.MAX_EXPERTS_DISPLAY,
      MAX_RELATED_TOPICS: TOPIC_DETAIL_CONSTANTS.MAX_RELATED_TOPICS
    }
  },
  computed: {
    expertMatches() {
      try {
        const matches = calculateExpertMatches(
          this.topic.title,
          this.context,
          this.experts.map(e => ({ id: e.id, topics: e.topics, tags: e.tags, resume: e.resume }))
        )
        return matches.slice(0, this.MAX_EXPERTS_DISPLAY)
      } catch (error) {
        console.error('专家匹配失败:', error)
        return []
      }
    },
    matchedExperts() {
      const matched = this.expertMatches
        .map(m => this.experts.find(e => e.id === m.expertId))
        .filter(Boolean)

      // 如果匹配专家不足，从剩余专家中补充，避免重复
      if (matched.length < this.MAX_EXPERTS_DISPLAY) {
        const matchedIds = new Set(matched.map(e => e.id))
        const remainingExperts = this.experts.filter(e => !matchedIds.has(e.id))
        const needCount = this.MAX_EXPERTS_DISPLAY - matched.length
        return [...matched, ...remainingExperts.slice(0, needCount)]
      }

      return matched
    },
    relatedTopics() {
      if (!this.topic.relatedIds || this.topic.relatedIds.length === 0) return []
      return this.topic.relatedIds
        .slice(0, this.MAX_RELATED_TOPICS)
        .map(id => TOPICS.find(t => t.id === id))
        .filter(Boolean)
    }
  },
  methods: {
    toggleExpand() {
      this.isExpanded = !this.isExpanded
    },
    scrollExperts(direction) {
      const container = document.getElementById('expert-scroll-container')
      if (container) {
        const scrollAmount = direction === 'left' ? -200 : 200
        container.scrollBy({ left: scrollAmount, behavior: 'smooth' })
      }
    },
    async handleCopy() {
      try {
        await navigator.clipboard.writeText(this.topic.caseStudy || '')
        this.addToast(TOPIC_DETAIL_MESSAGES.toast.copySuccess, 'success')
      } catch {
        this.addToast('复制失败，请手动复制', 'error')
      }
    },
    handleLike() {
      this.addToast(TOPIC_DETAIL_MESSAGES.toast.likeSuccess, 'success')
    },
    handleDislike() {
      this.feedbackModalOpen = true
    },
    handleFeedbackSubmit(reason, detail) {
      console.log('反馈内容:', { reason, detail })
      this.addToast(TOPIC_DETAIL_MESSAGES.toast.feedbackSuccess, 'success')
    },
    handleBookmark() {
      this.addToast(TOPIC_DETAIL_MESSAGES.toast.bookmarkSuccess, 'success')
    },
    handleContribute() {
      this.contributeModalOpen = true
    },
    handleContributeSubmit(content) {
      console.log('用户支招:', content)
      this.addToast(TOPIC_DETAIL_MESSAGES.toast.contributeSuccess, 'success')
    },
    handleNavigateToChat() {
      this.$router.push(`/diagnose-start?topic=${this.topic.id}`)
    },
    handleNavigateToPractice() {
      this.$router.push(`/practice?topic=${this.topic.id}`)
    },
    navigateToExpert(expertId) {
      this.$router.push(`/expert/${expertId}`)
    },
    navigateToExpertCase(expertId, type) {
      this.$router.push(`/expert/${expertId}/case/c1?type=${type}&from=topic`)
    },
    navigateToTopic(topicId) {
      this.$router.push(`/topic/${topicId}`)
    },
    addToast(message, type = 'info') {
      const id = Date.now().toString()
      this.toasts.push({ id, message, type })
      setTimeout(() => {
        this.removeToast(id)
      }, 2000)
    },
    removeToast(id) {
      this.toasts = this.toasts.filter(t => t.id !== id)
    }
  }
}
</script>

<style scoped>
@keyframes fadeInUp {
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in-up {
  animation: fadeInUp 0.5s ease-out forwards;
}
</style>
